import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const CATEGORIES = ["All", "Ceramics", "Leather", "Candles", "Deskware"];
const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "price-asc", label: "Price — Low to High" },
  { id: "price-desc", label: "Price — High to Low" },
  { id: "name", label: "Alphabetical" },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    let list = category === "All" ? [...products] : products.filter((p) => p.category === category);
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => Number(b.featured) - Number(a.featured));
    return list;
  }, [products, category, sort]);

  return (
    <div className="px-6 pb-24 pt-36 sm:px-12 lg:px-20" data-testid="shop-page">
      <Reveal>
        <p className="eyebrow-label mb-4">The Catalog</p>
        <h1 className="font-display text-4xl font-light leading-tight text-bone sm:text-5xl lg:text-6xl">
          Objects awaiting a keeper.
        </h1>
      </Reveal>

      <div className="mt-14 flex flex-wrap items-center justify-between gap-6 border-y border-white/10 py-5">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`filter-${c.toLowerCase()}`}
              className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${
                category === c
                  ? "border-vermilion bg-vermilion text-obsidian"
                  : "border-white/15 text-smoke hover:border-bone hover:text-bone"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="sort-select"
          className="border border-white/15 bg-obsidian px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-smoke outline-none"
        >
          {SORTS.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="pt-24 text-center font-mono text-xs uppercase tracking-[0.3em] text-faint">Unwrapping the collection…</p>
      ) : visible.length === 0 ? (
        <p className="pt-24 text-center font-display text-2xl italic text-faint">Nothing in this chapter yet.</p>
      ) : (
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {visible.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i % 4} />
          ))}
        </div>
      )}
    </div>
  );
}
